'use strict';
import $ from 'jquery';

import { eventEmitter } from "../global/eventEmitter";

var defaultTemplate = require('../templates/modalTemplate.hbs');  

/**
 * @fileOverview
 *
 * ModalView
 *
 * @author Jim Schofield
 */


/**
 * Creates a modal that takes either content on the page or a passed jQuery object as content
 * 
 *
 * @class ModalView
 * @param {jQuery} {object} pass in content and options object
 *
 * @constructor
 */

/**
 * 
 * data-modal-selector attribute will point to data already on page.
 * 
 * options structure:
 *  {
 *      classExtension: String (added to container to modify styles)
 *      template: String (path to handlbars template from project base directory)
 *      modalClickOut: String (no clickout is default behavior)
 *  }
 */


export default class ModalView {
    constructor($trigger, $content, options) {

        /**
         * A reference to the trigger DOM element
         *
         * @property $trigger
         * @type {jQuery}
         * @public
         */
        if ($trigger instanceof $) { // jshint ignore:line
            this.$trigger = $trigger;
        } else {
            throw ('Trigger element is not a jquery element.  Defaulting to no trigger object on document.')
            this.$trigger = null;
        }

        /**
         * A reference to the content to be placed in the modal
         *   if a $ object, use that
         *   if not $ object try to access string selector from data attribute
         *   else throw error
         *
         * @property $content
         * @type {jQuery}
         * @public
         */
        if ($content instanceof $) {
            this.$content = $content;
        } else if (!$content) {
            var $inlineModalContent = $(this.$trigger.data('modal-selector'));

            if ($inlineModalContent.length) {
                this.$content = $($inlineModalContent);
            } else {
                throw ('ModalView: $content selector does not find content on the page')
            }
        } else {
            throw ('ModalView: $content is not a jquery object or valid selector')
        }

        /**
         * A reference to the config options
         *
         * @default options
         * @property isEnabled
         * @type {Boolean}
         * @public
         */
        this.options = options;

        /**
         * Tracks whether the view is enabled  
         *
         * @default false
         * @property isEnabled
         * @type {Boolean}
         * @public
         */
        this.isEnabled = false;

        /**
         * Tracks whether the modal is currently showing
         *
         * @default false
         * @property isVisible
         * @type {Boolean}
         * @public
         */
        this.isVisible = false;

        /**
         * A reference to the focused element before modal was opened
         *
         * @default null
         * @property oldFocus
         * @type {Boolean}
         * @public
         */
        this.oldFocus = null;

        /**
         * Screen position from when modal opens
         *
         * @default null
         * @property oldFocus
         * @type {Boolean}
         * @public
         */
        this.screenPosition = null;

        /**
         * A list of all possible focusable selectors for jquery
         *
         * @default A really long string
         * @property focusableSelectorList
         * @type {String}
         * @public
         */
        this.focusableSelectorList = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, *[tabindex], *[contenteditable]'

        this.init();
    };

    /**
     * Initializes the UI Component View
     *
     * @method init
     * @public
     * @chainable
     */
    init() {

        this.parseOptions()
            .createTemplate()
            .setupHandlers()
            .createChildren()
            .enable();

        return this;
    };


    /**
     *  Finds options from options object or data attributes and formats the options object
     *
     * @method parseOptions
     * @public
     * @chainable
     */
    parseOptions() {

        if (typeof this.options === "undefined") {
            this.options = {};
        }

        // checks for data attributes and saves value to options object
        var checkForParam = function (name) {
            //convert to kebab case
            var kebabName = name.replace(/([a-z][A-Z])/g, function (g) {
                return g[0] + '-' + g[1].toLowerCase()
            });

            // checking data attribute
            var tempValue = this.$trigger.attr('data-' + kebabName);
            if (typeof tempValue !== 'undefined') {

                //coerce true or false strings into values
                if (tempValue === "true" || tempValue === "false") {
                    this.options[name] = (tempValue == 'true');
                } else {
                    // else just store the string
                    this.options[name] = tempValue;
                }
            }
        }.bind(this);
        // check for specific data attribute options parameters
        checkForParam('classExtension');
        checkForParam('template');
        checkForParam('modalClickOut');

        // merge any found keys in options or data attributes into class options object
        this.options = $.extend({
                classExtension: null,
                template: '../templates/modalTemplate.hbs',
                modalClickOut: null,
            },
            this.options
        );

        return this;
    };



    /**
     *  Sets up container to receive content, pick content off the dom, or creates default container if no element is provided
     *
     * @method setupContainer
     * @public
     * @chainable
     */
    createTemplate() {

        this.hbsTemplate = require(this.options.template)

        this.htmlTemplate = this.hbsTemplate({
            content: this.$content[0].outerHTML,
            classExtension: this.options.classExtension
        });
        this.$template = $(this.htmlTemplate);

        return this;
    };

    /**
     * Binds the scope of any handler functions
     * Should only be run on initialization of the view  
     *
     * @method setupHandlers
     * @public
     * @chainable
     */
    setupHandlers() {

        if (this.$trigger) {
            this.handleTargetClick = this.toggle.bind(this);
        }

        this.handleClickOut = this.clickOut.bind(this);

        this.handleClose = this.close.bind(this);

        return this;
    };


    /**
     * close if clicking outside of the modal content
     *
     * @method clickOut
     * @public
     * @chainable
     */
    clickOut(e) {
        if (!$(e.target).closest('.js-modal-content').length) {
            this.close();
        }
        return this;
    }

    /**
     * Create any child objects or references to DOM elements
     * Should only be run on initialization of the view
     *
     * @method createChildren
     * @public
     * @chainable
     */
    createChildren() {


        return this;
    };

    /**
     * Enables the component
     * Performs any event binding to handlers
     *
     * @method enable
     * @public
     * @chainable
     */
    enable() {
        if (this.isEnabled) {
            return this;
        }

        if (this.$trigger) {
            this.$trigger.on('click', this.handleTargetClick)
        }

        this.isEnabled = true;
        return this;
    };

    /**
     * Attach close handlers
     *
     * @method enable
     * @public
     * @chainable
     */
    onEnable() {
        //close events
        if (this.options.modalClickOut) {
            //click out option enabled
            $('.js-modal').on('click', this.handleClickOut);
        } else {
            //click out disabled, only close on button click
            $('.js-modal-close').on('click', this.handleClose);
        }

        //key management events
        $('.js-modal').on('keydown', function (e) {
            if (e.keyCode === 27) {
                this.close();
            }
            //tab cylcing - trap focus within the modal
            var currentFocus = $(e.target);
            var focusableList = $('.js-modal').find(this.focusableSelectorList);
            var lastFocusableElement = focusableList.last()[0];
            var firstFocusableElement = focusableList.first()[0];

            if (e.keyCode === 9 && !e.shiftKey) { //tab key pressed
                if (document.activeElement === lastFocusableElement) {
                    firstFocusableElement.focus();
                    e.preventDefault();
                }
            }
            if (e.keyCode === 9 && e.shiftKey) {
                if (document.activeElement === firstFocusableElement) {
                    lastFocusableElement.focus();
                    e.preventDefault();
                }
            }
        }.bind(this));

        eventEmitter.emit('modal-open',this.$template);
    }

    /**
     * toggles modal
     *
     * @method toggleModal
     * @public
     * @chainable
     */
    toggle(e) {
        e.preventDefault();
        if (this.isVisible) {
            this.close();
        } else {
            this.open();
        }
        return this;
    };

    /**
     * open modal
     *
     * @method open
     * @public
     * @chainable
     */
    open(e) {

        //store focus from before modal is open
        this.oldFocus = $(':focus');

        // store screen position and set site position so screen does not move
        this.screenPosition = $(window).scrollTop();

        // freeze the body
        $('body').addClass('modal-is-active');
        $('.site').css("top", -this.screenPosition);

        // add modal
        $('.site').append(this.$template);

        // set focus to first focusable element in modal
        $('.modal').find(this.focusableSelectorList).eq(0).focus();

        this.onEnable();

        this.isVisible = true;

        return this;
    };

    /**
     * close modal
     *
     * @method close
     * @public
     * @chainable
     */
    close() {

        //restore original focus on document
        this.oldFocus.focus();

        // unfreeze body
        $('body').removeClass('modal-is-active');
        $('.site').css("top", 'auto');
        $(document).scrollTop(this.screenPosition);

        //remove modal
        $('.site').find('.js-modal').remove();

        // tear down handlers for this mobile
        this.disable();

        this.isVisible = false;

        return this;
    };

    /**
     * Disables the component
     * Tears down any event binding to handlers
     *
     * @method disable
     * @public
     * @chainable
     */
    disable() {
        if (!this.isEnabled) {
            return this;
        }

        $('.js-modal').off();
        $('.js-modal-close').off();

        this.isEnabled = false;

        return this;
    };

}